import {
  BlotterTable,
  Box,
  type ColumnDef,
  formattedUTCDay,
  type MinimalSubscriptionResponse,
  type SizeColumnParams,
  useBehaviorSubject,
  useBlotterTable,
} from '@talos/kyoko';
import type { GridOptions } from 'ag-grid-community';
import { compact } from 'lodash-es';
import { useMemo } from 'react';
import type { CareOrderParser, ImportedCareOrder } from '../types';
import { SIMILAR_CARE_ORDERS_COL_WIDTHS } from './SimilarCareOrdersBlotter';

interface ImportedCareOrderBlotterProps {
  importedCareOrder: ImportedCareOrder;
  parser: CareOrderParser;
}

export const ImportedCareOrderBlotter = ({ importedCareOrder, parser }: ImportedCareOrderBlotterProps) => {
  const { observable: importedCareOrderDataObs } = useBehaviorSubject(
    () =>
      ({
        initial: true,
        data: importedCareOrder ? [importedCareOrder] : [],
      }) satisfies MinimalSubscriptionResponse<ImportedCareOrder>,
    [importedCareOrder]
  );

  const importedCareOrderBlotterGridOptions: GridOptions<ImportedCareOrder> = useMemo(
    () => ({
      defaultColDef: {
        suppressHeaderMenuButton: true, // dont allow modifying columns in this dialog
      },
      rowSelection: {
        checkboxes: true,
        mode: 'singleRow',
        enableClickSelection: false,
        hideDisabledCheckboxes: true,
        isRowSelectable: () => false, // we dont actually allow row selection, just need the width to line up the 2 blotters
      },
    }),
    []
  );

  const importedCareOrderColumns = useImportedCareOrderColumns(parser);
  const importedCareOrderBlotter = useBlotterTable({
    dataObservable: importedCareOrderDataObs,
    errorObservable: undefined,
    rowID: 'ClOrdID' satisfies keyof ImportedCareOrder,
    columns: importedCareOrderColumns,
    gridOptions: importedCareOrderBlotterGridOptions,
  });

  return (
    <Box h="80px">
      {/* 80 is enough space for header, 1 row + scroll bar */}
      <BlotterTable {...importedCareOrderBlotter} background="backgroundModal" />
    </Box>
  );
};

const useImportedCareOrderColumns = (parser: CareOrderParser) => {
  return useMemo(
    () =>
      compact([
        {
          type: 'custom',
          id: 'warning', // does nothing, just spaces to align columns between the two blotters
          title: '',
          width: SIMILAR_CARE_ORDERS_COL_WIDTHS.warning,
        },
        parser === 'aladdin'
          ? {
              type: 'security',
              field: 'Symbol',
              title: 'Symbol',
              width: SIMILAR_CARE_ORDERS_COL_WIDTHS.symbol,
            }
          : {
              type: 'text',
              field: 'Annotations.BasketOrder.BasketSymbol',
              title: 'Fund',
              width: SIMILAR_CARE_ORDERS_COL_WIDTHS.fund,
            },
        {
          type: 'side',
          field: 'Side',
          width: SIMILAR_CARE_ORDERS_COL_WIDTHS.side,
        },
        parser === 'aladdin'
          ? {
              type: 'size',
              field: 'OrderQty',
              title: 'Order Qty',
              width: SIMILAR_CARE_ORDERS_COL_WIDTHS.orderQty,
              params: {
                currencyField: 'Currency',
              } satisfies SizeColumnParams<ImportedCareOrder>,
            }
          : {
              type: 'number',
              field: 'units',
              title: 'Units',
              width: SIMILAR_CARE_ORDERS_COL_WIDTHS.units,
            },
        {
          type: 'custom',
          id: 'statusSpacing', // ImportedCareOrders dont have status. We just space here for alignment.
          title: 'Status',
          width: SIMILAR_CARE_ORDERS_COL_WIDTHS.status,
        },
        {
          type: 'custom',
          id: 'submitTimeSpacing', // ImportedCareOrders dont have submittime. We just space here for alignment.
          title: 'Submit Time',
          width: SIMILAR_CARE_ORDERS_COL_WIDTHS.submitTime,
        },
        {
          type: 'text',
          field: 'Group',
          width: SIMILAR_CARE_ORDERS_COL_WIDTHS.group,
        },
        {
          type: 'text',
          field: 'Annotations.ETP.InitiatingFrom',
          title: 'Initiating From',
          width: SIMILAR_CARE_ORDERS_COL_WIDTHS.initiatingFrom,
        },
        {
          type: 'text',
          field: 'Counterparty',
          title: 'Counterparty',
          width: SIMILAR_CARE_ORDERS_COL_WIDTHS.counterparty,
        },
        parser === 'unqork' && {
          type: 'date',
          field: 'SettleDate',
          title: 'Settle Date',
          params: {
            customFormatter: formattedUTCDay,
          },
          width: SIMILAR_CARE_ORDERS_COL_WIDTHS.settleDate,
        },
      ] satisfies (ColumnDef<ImportedCareOrder> | false)[]),
    [parser]
  );
};
